import { flip, offset, shift, useFloating } from '@floating-ui/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { formatPercentage } from '../../lib/formatters';
import { round } from '../../lib/math';
import { HALF_THUMB_WIDTH, THUMB_WIDTH, TICK_OFFSET } from './constants';
import { useSliderInteractions } from './useSliderInteractions';
import { calculateTickPositions, getSliderValueFromPct, getThumbPosition } from './utils';

export interface BaseSliderProps {
  value: bigint;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
  onChange: (value: bigint) => void;
  formatValue?: (value: bigint) => string;
}

export interface SliderConfig {
  min: number;
  max: number;
  step: number;
  ticks: Array<{ value: number; label?: string }>;
  normalizeValue: (value: bigint) => number;
  denormalizeValue: (value: number) => bigint;
  getPercentage: (normalizedValue: number) => number;
}

const defaultFormatter = (value: bigint) =>
  formatPercentage(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface BaseSliderComponentProps extends BaseSliderProps {
  config: SliderConfig;
  showLabels?: boolean;
  minLabel?: string;
  maxLabel?: string;
  renderTickLabel?: (
    tick: { value: number; label?: string },
    index: number,
    isHovered: boolean | null
  ) => React.ReactNode;
}
export const BaseSlider: React.FC<BaseSliderComponentProps> = ({
  value,
  disabled = false,
  className,
  ariaLabel,
  ariaDescribedby,
  onChange,
  formatValue = defaultFormatter,
  config,
  showLabels = true,
  minLabel,
  maxLabel,
  renderTickLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);

  const normalizedValue = config.normalizeValue(value);
  const percentValue = config.getPercentage(normalizedValue);
  const numberOfTicks = config.ticks.length;

  const tickPositions = useMemo(
    () => calculateTickPositions(containerWidth, numberOfTicks),
    [containerWidth, numberOfTicks]
  );
  const thumbPosition = useMemo(
    () => getThumbPosition(containerWidth, percentValue),
    [containerWidth, percentValue]
  );

  const { hoveredTick, isThumbHovered, handleMouseMove, handleMouseLeave } = useSliderInteractions(
    containerRef,
    tickPositions,
    thumbPosition,
    numberOfTicks,
    isInteracting
  );

  const showTooltip = !disabled && (isThumbHovered || isInteracting);
  const showCursorAsPointer = !disabled && (hoveredTick || isThumbHovered || isInteracting);

  // Tooltip floating ui setup
  const { x, y, strategy, refs, update } = useFloating({
    placement: 'top',
    open: showTooltip,
    middleware: [offset(20), flip(), shift({ padding: 8 })],
  });

  // Calculate progress bar positioning
  const progressStartPosition = HALF_THUMB_WIDTH;
  const progressWidth = Math.max(0, thumbPosition - progressStartPosition);

  // Handle container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Handle touch/mouse drag
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || !containerRef.current) return;

      e.preventDefault();
      setIsInteracting(true);

      const updateValue = (clientX: number, snapToTick: boolean = true) => {
        if (!containerRef.current) return;

        // If we are hovering over a tick, set the value to the tick value (unless it's the same as the current value)
        if (snapToTick && hoveredTick && round(hoveredTick.pct, 2) !== round(percentValue, 2)) {
          // use percent value from the hovered tick to snap to it
          const newValue = getSliderValueFromPct(config, hoveredTick.pct);
          onChange(newValue);
          update(); // Update floating UI position
          return;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const newValue = getSliderValueFromPct(config, percentage);

        onChange(newValue);
        update(); // Update floating UI position
      };

      updateValue(e.clientX);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        updateValue(moveEvent.clientX, false);
      };

      const handlePointerUp = () => {
        setIsInteracting(false);
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [disabled, config, onChange, update, hoveredTick, percentValue]
  );

  return (
    <div className="mx-4 touch-none select-none sm:m-0">
      <div
        ref={containerRef}
        className={twMerge(
          'relative h-6 w-full touch-none select-none',
          disabled && 'opacity-50',
          className
        )}
        role="group"
        data-testid="slider-container"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Pointer overlay for better touch interactions */}
        <div
          className={twJoin('absolute inset-0 z-20 h-10', showCursorAsPointer && 'cursor-pointer')}
          onPointerDown={handlePointerDown}
          data-testid="slider-overlay"
        />

        {/* Track */}
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 bg-contentSecondary"
          style={{
            left: `${HALF_THUMB_WIDTH - TICK_OFFSET}px`,
            width: `calc(100% - ${THUMB_WIDTH}px)`,
          }}
          role="presentation"
          data-testid="slider-track"
        >
          {/* Progress bar */}
          <div
            className="absolute h-full bg-contentBlue"
            style={{
              left: `${progressStartPosition}px`,
              width: `${progressWidth}px`,
            }}
            role="presentation"
          />
        </div>

        {/* Tick marks */}
        <div className="absolute inset-0" role="presentation">
          {tickPositions.map((tickPos, index) => {
            const tick = config.ticks[index];
            if (!tick) return null;

            const tickPercentage = numberOfTicks > 1 ? index / (numberOfTicks - 1) : 0;
            const hasPassedTick = percentValue >= tickPercentage;
            const isHovered = hoveredTick && hoveredTick.pct === tickPercentage;

            return (
              <div
                key={`tick-${index}`}
                className={twJoin('group absolute flex h-full w-3 items-center justify-center')}
                style={{ left: `${tickPos - HALF_THUMB_WIDTH}px` }}
                tabIndex={disabled ? -1 : 0}
                role="button"
                data-testid={`slider-tick-${index}`}
                aria-label={`Set to ${tick.label || tick.value}`}
              >
                <div
                  className={twJoin(
                    'size-2 rounded-full transition-opacity duration-150',
                    hasPassedTick ? 'bg-contentBlue' : 'bg-contentSecondary'
                  )}
                  data-testid="slider-tick"
                />
                {renderTickLabel && (
                  <div className="absolute mt-7 px-2 py-1">
                    {renderTickLabel(tick, index, isHovered)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Range input */}
        <input
          ref={inputRef}
          type="range"
          min={config.min}
          max={config.max}
          step={config.step}
          value={normalizedValue}
          readOnly
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          aria-valuemin={config.min}
          aria-valuemax={config.max}
          aria-valuenow={normalizedValue}
          aria-valuetext={formatValue(value)}
          data-testid="slider-thumb"
          className={twJoin(
            'absolute inset-0 size-full appearance-none bg-transparent',
            'pointer-events-none', // disabled since we use the overlay

            // Firefox
            '[&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:bg-backgroundBase [&::-moz-range-thumb]:ring-2 [&::-moz-range-thumb]:ring-backgroundPrimary',
            disabled
              ? '[&::-moz-range-thumb]:cursor-default [&::-moz-range-thumb]:border-contentTertiary'
              : '[&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-contentBlue',

            // Webkit
            '[&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:bg-backgroundBase [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-backgroundPrimary',
            disabled
              ? '[&::-webkit-slider-thumb]:cursor-default [&::-webkit-slider-thumb]:border-contentTertiary'
              : '[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:border-contentBlue',

            disabled && 'cursor-default'
          )}
        />

        {/* Floating UI reference point */}
        <div
          ref={refs.setReference}
          className="pointer-events-none absolute top-1/2"
          style={{ left: `${thumbPosition}px` }}
        />

        {/* Tooltip */}
        {showTooltip && (
          <div
            ref={refs.setFloating}
            className="z-20 rounded-md bg-neutral-800 px-3 py-1 text-sm font-medium text-white shadow-lg"
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
          >
            {formatValue(value)}
          </div>
        )}
      </div>

      {/* Labels */}
      {showLabels && (minLabel || maxLabel) && (
        <div className="flex justify-between text-xs text-contentSecondary">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
};
